import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ElementRef,
  HostListener,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface AppMultiSelectOption {
  value: any;
  label: string;
  disabled?: boolean;
  icon?: string;
  sublabel?: string;
}

@Component({
  selector: 'app-multi-select',
  templateUrl: './app-multi-select.component.html',
  styleUrls: ['./app-multi-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppMultiSelectComponent),
      multi: true,
    },
  ],
})
export class AppMultiSelectComponent implements ControlValueAccessor, OnChanges {
  @Input() labelText: string = '';
  @Input() label: string = '';
  @Input() mandatory: boolean = false;
  @Input() required: boolean = false;
  @Input() optional: boolean = false;
  @Input() tooltip: string = '';
  @Input() labelIcon: string = '';
  @Input() labelSize: 'sm' | 'md' | 'lg' = 'md';

  get resolvedLabel(): string {
    return this.labelText || this.label || '';
  }

  get isRequired(): boolean {
    return this.mandatory || this.required;
  }

  @Input() options: any[] = [];
  @Input() placeholder: string = 'Select...';
  @Input() disabled: boolean = false;
  @Input() errorMsg: string | null = null;
  @Input() helperText: string = '';
  @Input() searchable: boolean = true;
  @Input() searchPlaceholder: string = 'Search...';
  @Input() maxSelected: number | null = null;

  @Input() size: 'sm' | 'md' | 'lg' | 'compact' = 'md';
  @Input() height: number | string = '';
  @Input() customClass: string = '';
  @Input() containerClass: string = '';

  @Output() selectionChange = new EventEmitter<any[]>();
  @Output() searchChange = new EventEmitter<string>();

  value: any[] = [];
  isOpen: boolean = false;
  searchTerm: string = '';
  formattedOptions: AppMultiSelectOption[] = [];

  private onChange: (value: any[]) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private elementRef: ElementRef, private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.formatOptions();
    }
  }

  private formatOptions(): void {
    if (!Array.isArray(this.options)) {
      this.formattedOptions = [];
      return;
    }

    this.formattedOptions = this.options.map((opt) => {
      if (opt !== null && typeof opt === 'object' && 'value' in opt && 'label' in opt) {
        return opt as AppMultiSelectOption;
      }
      return {
        value: opt,
        label: String(opt),
      };
    });
  }

  writeValue(val: any): void {
    this.value = Array.isArray(val) ? [...val] : [];
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.isOpen = false;
    }
    this.cdr.markForCheck();
  }

  toggleDropdown(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.searchTerm = '';
    } else {
      this.onTouched();
    }
  }

  isSelected(val: any): boolean {
    if (!Array.isArray(this.value)) return false;
    return this.value.some((item) => item === val || JSON.stringify(item) === JSON.stringify(val));
  }

  isOptionDisabled(opt: AppMultiSelectOption): boolean {
    if (opt.disabled) return true;
    if (
      this.maxSelected !== null &&
      this.value.length >= this.maxSelected &&
      !this.isSelected(opt.value)
    ) {
      return true;
    }
    return false;
  }

  toggleOption(opt: AppMultiSelectOption, event?: any): void {
    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }
    if (this.disabled || this.isOptionDisabled(opt)) return;

    const exists = this.isSelected(opt.value);
    let updated: any[];

    if (exists) {
      updated = this.value.filter(
        (item) => item !== opt.value && JSON.stringify(item) !== JSON.stringify(opt.value)
      );
    } else {
      updated = [...this.value, opt.value];
    }

    this.value = updated;
    this.onChange(this.value);
    this.selectionChange.emit(this.value);
    this.onTouched();
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  removeOption(val: any, event?: any): void {
    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }
    if (this.disabled) return;

    this.value = this.value.filter(
      (item) => item !== val && JSON.stringify(item) !== JSON.stringify(val)
    );
    this.onChange(this.value);
    this.selectionChange.emit(this.value);
    this.onTouched();
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  onSearchInput(event: any): void {
    this.searchTerm = event.target.value || '';
    this.searchChange.emit(this.searchTerm);
  }

  get selectedOptions(): AppMultiSelectOption[] {
    if (!Array.isArray(this.value) || this.value.length === 0) return [];
    return this.value.map((v) => {
      const match = this.formattedOptions.find(
        (o) => o.value === v || JSON.stringify(o.value) === JSON.stringify(v)
      );
      return match || { value: v, label: String(v) };
    });
  }

  get filteredOptions(): AppMultiSelectOption[] {
    if (!this.searchTerm || !this.searchable) {
      return this.formattedOptions;
    }
    const term = this.searchTerm.toLowerCase();
    return this.formattedOptions.filter(
      (opt) =>
        opt.label.toLowerCase().includes(term) ||
        (opt.sublabel && opt.sublabel.toLowerCase().includes(term))
    );
  }

  get hasError(): boolean {
    return !!this.errorMsg && this.errorMsg.trim().length > 0;
  }

  get resolvedHeight(): string | null {
    if (this.height) {
      return typeof this.height === 'number' ? `${this.height}px` : this.height;
    }
    return null;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      if (this.isOpen) {
        this.isOpen = false;
        this.onTouched();
      }
    }
  }
}
